import { useEffect, useState } from "react";
import type { SessionInfo, UUID } from "../sync/types";
import { ago, browserOf } from "../agent";
import { Badge } from "./ui";
import { IconDesktop } from "./icons";
import { locale, t } from "../i18n";

// SessionList lists where someone is signed in, most recently used first,
// with a sign-out for each: one's own, but for the browser asking, or, for
// an admin, anyone's.
export function SessionList({ load, end, label }: { load: () => Promise<SessionInfo[]>; end: (id: UUID) => Promise<unknown>; label: string }) {
  const [list, setList] = useState<SessionInfo[] | null>(null);
  const [error, setError] = useState("");
  const failed = (err: unknown) => setError(err instanceof Error ? err.message : String(err));
  const reload = () => load().then(setList, failed);
  // Once: a new list for someone else is a new SessionList.
  useEffect(() => {
    void reload();
  }, []);

  async function signOut(s: SessionInfo) {
    setError("");
    try {
      await end(s.id);
      await reload();
    } catch (err) {
      failed(err);
    }
  }

  if (!list) return error ? <p className="error">{error}</p> : <p className="muted">{t("Loading…")}</p>;
  return (
    <>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {list.length === 0 ? (
        <p className="muted">{t("Signed in nowhere.")}</p>
      ) : (
        <ul className="rows session-list" aria-label={label}>
          {list.map((s) => (
            <li key={s.id}>
              <span className="session-icon" aria-hidden>
                <IconDesktop size={18} />
              </span>
              <span className="row-main">
                <span className="row-title">
                  {browserOf(s.user_agent)} {s.current && <Badge tone="ok">{t("This browser")}</Badge>}
                </span>
                <span className="row-meta">
                  {[
                    s.ip
                      ? t("signed in {date} from {ip}", { date: new Date(s.created_at).toLocaleDateString(locale, { dateStyle: "medium" }), ip: s.ip })
                      : t("signed in {date}", { date: new Date(s.created_at).toLocaleDateString(locale, { dateStyle: "medium" }) }),
                    s.last_used_at ? t("active {when}", { when: ago(s.last_used_at) }) : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              {!s.current && (
                <button className="small ghost" onClick={() => void signOut(s)} aria-label={t("Sign out {where}", { where: [browserOf(s.user_agent), s.ip].filter(Boolean).join(", ") })}>
                  {t("Sign out")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
